import { useCallback, useEffect, useMemo, useState } from "react";

import { IS_PROD_BUILD } from "@/constants/env";
import { Routes as AppRoutes } from "@/constants/routes";
import { Consent } from "@/types/common";
import { AmplitudeTracker, PaltaDataPlatformTracker, Sentry, TagManager } from "@/utils/trackers";

const IS_DEV = !IS_PROD_BUILD;
enum TrackersTypes {
    marketing = "marketing",
    preferences = "preferences",
    statistics = "statistics",
}
const trackersByType = {
    [TrackersTypes.marketing]: [TagManager],
    [TrackersTypes.statistics]: [AmplitudeTracker, PaltaDataPlatformTracker],
    [TrackersTypes.preferences]: [Sentry],
};

const useConsent = () => {
    const [consent, setConsent] = useState<Consent | null>();

    const initTrackers = (type: TrackersTypes) => {
        trackersByType[type].forEach((tracker) => tracker.init());
    };

    const stopTrackers = (type: TrackersTypes) => {
        trackersByType[type].forEach((tracker) => tracker.stop());
    };

    const initAllTrackers = () => {
        initTrackers(TrackersTypes.marketing);
        initTrackers(TrackersTypes.statistics);
        initTrackers(TrackersTypes.preferences);
    };

    const saveConsent = useCallback(() => {
        setConsent(window.Cookiebot?.consent ? { ...window.Cookiebot?.consent } : null);
    }, []);

    const isFunnelFox = useMemo(() => window.location.pathname.includes(AppRoutes.FFPaywall), []);

    useEffect(() => {
        const { method, stamp } = consent || {};
        const noConsentOnDomain =
            method === null && window.Cookiebot?.consentLifetime === null && stamp === "0";

        const initAll = noConsentOnDomain || IS_DEV || isFunnelFox;
        if (initAll) {
            initAllTrackers();
        } else {
            Object.values(TrackersTypes).forEach((trackersType) => {
                consent?.[trackersType] ? initTrackers(trackersType) : stopTrackers(trackersType);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consent]);

    useEffect(() => {
        saveConsent();
        window.addEventListener("CookiebotOnDecline", saveConsent);
        window.addEventListener("CookiebotOnAccept", saveConsent);

        return () => {
            window.removeEventListener("CookiebotOnDecline", saveConsent);
            window.removeEventListener("CookiebotOnAccept", saveConsent);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
};

export default useConsent;
